import { PrismicRichText } from '@prismicio/react';
import { Disclosure, DisclosurePanel } from '@reach/disclosure';
import { Radio } from '@remote-com/mushu/src/components/Ui/Form/Radio/Radio';
import { Formik } from 'formik';
import { useRouter } from 'next/router';
import { RichText } from 'prismic-reactjs';
import { useState } from 'react';
import { Button } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { TwoStepFormModalButton } from 'remote-com-shared/components/TwoStepForm';
import { fluidProp } from 'remote-com-shared/style/helpers';
import { breakpointFrom } from 'remote-com-shared/style/mediaQueries';
import styled, { css } from 'styled-components';

import { autoApplyPromocode } from '@/src/components/Header/PartnerNavigationMenu';
import { PricingDialog } from '@/src/domains/pricing/PricingDialog';
import {
  Section,
  BaseCell,
  TitleCell,
  PriceCell,
  ColumnHighlightMessage,
  BillingFrequencyCell,
  FeatureCellContent,
  FeatureCellIcon,
  CTACell,
  FooterCell,
  SimplePriceText,
  ScreamPriceText,
  ContractorPriceWrapper,
  SpecialPriceStart,
  SpecialPriceBadge,
  SpecialPriceValue,
  SpecialPriceDescription,
  BillingFrequencyControls,
  CTAButton,
  InternationalPricingButton,
} from '@/src/domains/pricing/PricingOverviewSection';
import { transformProperties } from '@/src/domains/prismic/helpers';
import { desktopBreakpoint } from '@/src/domains/shared/breakpoints';
import { GlobalPayrollFormModal } from '@/src/views/Platform/GlobalPayrollForm';

export const GET_STARTED_URL = `${process.env.NEXT_PUBLIC_EMPLOY_BASE_URL}/sign-up`;
export const GET_QUOTE_URL = `${process.env.NEXT_PUBLIC_PANDA_BASE_URL}/contact-us`;

export const PricingSection = styled(Section)`
  padding-bottom: 67px;
`;

const DisclaimerContainer = styled.div`
  margin-top: 36px;
  font-size: 14px;
`;

const OPEN_FORM_URL_PARAM_MAP = {
  scheduleDemo: 'schedule_demo_form',
};

function toggleFormIfParam(query, formKey) {
  return !!query[OPEN_FORM_URL_PARAM_MAP[formKey]];
}

const breakpointReference = breakpointFrom.lg;

export const PricingTable = styled.div`
  --cellSpacingX: 22px;
  --cellSpacingY: 16px;
  --edgeSpacing: 50px;
  --cardSpacingX: 24px;
  --cardSpacingY: 32px;
  --intervalSelectorSpacing: 25px;
  --buttonColor: #fff;
  --buttonBackground: var(--colors-irisBlue);

  display: flex;
  flex-direction: column;
  padding-top: 27px;
  gap: 20px;

  ${breakpointReference(css`
    display: grid;
    grid-template-columns: repeat(2, [column] 1fr) [end];
    gap: 20px;
  `)}
`;

export const BillingFrequencyPaddingBottom = styled(BillingFrequencyCell)`
  padding-bottom: 20px;
  padding-top: 0;
`;

const ButtonAdditionalPadding = styled.div`
  padding-bottom: 30px;

  ${desktopBreakpoint(css`
    padding-bottom: 0;
  `)}
`;

const PricingTitleCell = styled(TitleCell)`
  ${fluidProp(['padding-top'], '20px', '34px')}
`;

const PricingPriceCell = styled(PriceCell)`
  padding-bottom: 25px;
  color: var(--colors-irisBlue);
`;

const PurpleIcon = styled(FeatureCellIcon)`
  color: var(--colors-irisBlue);
`;

const DescriptionWrapper = styled.p`
  padding-bottom: 20px;
  padding-top: 0;
`;

const DescriptionCell = styled(BaseCell)`
  text-align: center;
  overflow: hidden; // Ensures margins dont open blank spaces

  p {
    margin-top: 0;
    margin-bottom: 30px;
  }

  ${DescriptionWrapper} {
    margin-bottom: 0;
    margin-top: 0;
  }
`;

const PricingButtonWrapper = styled.p`
  margin-top: 0;
  padding-bottom: 10px;

  ${desktopBreakpoint(css`
    margin-bottom: 0;
    padding-bottom: 35px;
  `)}
`;

const FeatureCell = styled(BaseCell)`
  padding-bottom: 19px;

  > *:first-child {
    margin-top: 0;
  }
  > *:last-child {
    margin-bottom: 0;
  }

  ${DescriptionCell} + & {
    padding-top: 15px;
  }

  p {
    margin: 0;
  }
`;

const PricingFeatureCellContent = styled(FeatureCellContent)`
  max-width: none;

  ${breakpointReference(css`
    max-width: 366px;
  `)}
`;

const PricingFeatureCellText = styled.p`
  ${({ $highlight }) =>
    !$highlight &&
    css`
      color: var(--colors-lynch);
    `}
`;

const PricingFooterCell = styled(FooterCell)`
  min-height: 66px;
  padding-bottom: 0;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
`;

export const Column = styled.div`
  position: relative;
  border: 1px solid rgba(119, 140, 163, 0.3);

  ${breakpointReference(css`
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    box-sizing: border-box;
    border-radius: 5px;

    &:hover {
      background-color: var(--colors-blank);
    }
  `)}

  ${({ $highlight }) =>
    $highlight &&
    css`
      margin-top: -29px;

      ${desktopBreakpoint(css`
        order: -1;
      `)}

      ${BaseCell} {
        --borderColor: #7764e8;
        color: var(--colors-selago);
        background-color: var(--colors-irisBlue);
        background-image: linear-gradient(270deg, rgb(89, 65, 215), rgb(73, 55, 177));
      }

      ${PricingTitleCell} {
        color: var(--colors-blank);
        padding-bottom: 18px;
        padding-top: 62px;
      }
    `};
`;

export const SpecialPriceWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: center;

  ${({ $crossed }) =>
    $crossed &&
    css`
      padding-left: 21px;
    `}
`;

export const PricingSpecialPriceBadge = styled(SpecialPriceBadge)`
  display: flex;
  align-items: center;
  margin-left: 2px;
`;

export const SecondPricingSpecialPriceBadge = styled(PricingSpecialPriceBadge)`
  && {
    color: var(--colors-irisBlue);
    ${({ theme }) => theme.typography.scale4}
    padding-top: 5px;
  }
`;

export const PricingSpecialPriceValue = styled(SpecialPriceValue)`
  ${({ $crossed }) =>
    $crossed &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: -8%;
        right: 5%;
        width: 110px;
        height: 3px;
        background-color: var(--colors-turquoise);
        transform: rotate(-12.53deg);
        transform-origin: center;
      }
    `}
`;

const CTACellWithHighlight = styled(CTACell)`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: flex-end;

  ${({ $highlight }) =>
    $highlight &&
    css`
      && > a {
        background-color: var(--colors-turquoise);
        color: var(--colors-irisBlue);
      }
    `};
`;

const DisclosurePanelStyled = styled(DisclosurePanel)`
  ${({ $isOpen }) =>
    $isOpen &&
    css`
      display: contents;
    `}
`;

const DisclosureButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 40px;
`;

export function PlanColumn({
  $highlight,
  $turquoiseIcon,
  title,
  price,
  startsOpen,
  billingFrequency,
  description,
  features,
  cta,
  openCtaLabel,
  footer,
  hidden,
}) {
  const [open, setOpen] = useState(startsOpen);

  if (hidden) {
    return null;
  }

  const handleOpenCard = () => {
    setOpen(!open);
  };

  return (
    <>
      {!hidden && (
        <Column $highlight={$highlight}>
          <PricingTitleCell>{title}</PricingTitleCell>
          <PricingPriceCell>{price}</PricingPriceCell>
          <DescriptionCell>{description}</DescriptionCell>
          {billingFrequency && (
            <BillingFrequencyPaddingBottom>{billingFrequency}</BillingFrequencyPaddingBottom>
          )}
          <Disclosure open={open}>
            {!open && (
              <DisclosureButtonWrapper>
                <Button tone="primary" shadow onClick={handleOpenCard}>
                  {openCtaLabel || 'Learn more'}
                </Button>
              </DisclosureButtonWrapper>
            )}
            <DisclosurePanelStyled $isOpen={open}>
              {features.map((feature, index) => (
                <FeatureCell key={index}>
                  <PricingFeatureCellContent>
                    {$turquoiseIcon ? <FeatureCellIcon /> : <PurpleIcon />}
                    <PricingFeatureCellText $highlight={$highlight}>
                      {feature}
                    </PricingFeatureCellText>
                  </PricingFeatureCellContent>
                </FeatureCell>
              ))}
              {!billingFrequency && (
                <ButtonAdditionalPadding>{billingFrequency}</ButtonAdditionalPadding>
              )}
              <CTACellWithHighlight $highlight={$highlight}>{cta}</CTACellWithHighlight>
              <PricingFooterCell>{footer}</PricingFooterCell>
            </DisclosurePanelStyled>
          </Disclosure>
        </Column>
      )}
    </>
  );
}

function PricingOverviewSectionPrice({ billingFrequency, ...cost }) {
  const {
    price_label: priceLabel,
    highlight_price_label: screamText,
    price_label_crossed: priceLabelCrossed,
    price_label_crossed_label: priceLabelCrossedLabel,
  } = cost;

  if (priceLabel) {
    if (screamText) {
      return (
        <ContractorPriceWrapper>
          <ScreamPriceText $crossed={priceLabelCrossed}>{priceLabel}</ScreamPriceText>
          {priceLabelCrossed && (
            <SecondPricingSpecialPriceBadge>
              {priceLabelCrossedLabel}
            </SecondPricingSpecialPriceBadge>
          )}
        </ContractorPriceWrapper>
      );
    }
    return <SimplePriceText $crossed={priceLabelCrossed}>{priceLabel}</SimplePriceText>;
  }

  const amounts = cost.amounts || [];
  const selectedAmount = amounts.find(({ label }) => label === billingFrequency);

  if (!selectedAmount) {
    return null;
  }

  const {
    title,
    amount,
    amount_per_unit: amountPerUnit,
    cross_amount: crossAmount,
    cross_label: crossLabel,
  } = selectedAmount;

  return (
    <div>
      <SpecialPriceStart>{title}</SpecialPriceStart>
      <SpecialPriceWrapper $crossed={crossAmount}>
        <PricingSpecialPriceBadge aria-describedby="free-price-description">
          {crossLabel}
        </PricingSpecialPriceBadge>
        <PricingSpecialPriceValue $crossed={crossAmount}>{amount}</PricingSpecialPriceValue>
      </SpecialPriceWrapper>
      <SpecialPriceDescription>{amountPerUnit}</SpecialPriceDescription>
    </div>
  );
}

function PricingOverviewSectionBillingFrequency({
  billingFrequency,
  setBillingFrequency,
  ...cost
}) {
  const { amounts } = cost;

  if (!(amounts.length > 1)) {
    return null;
  }

  return (
    <BillingFrequencyControls>
      <Formik
        initialValues={{
          billingFrequency,
        }}
      >
        {({ handleChange }) =>
          amounts.map(({ label, description }, index) => (
            <Radio
              key={index}
              name="billingFrequency"
              value={label}
              label={label}
              description={description || ''}
              onChange={(event) => {
                handleChange(event);
                setBillingFrequency(event.target.value);
              }}
            />
          ))
        }
      </Formik>
    </BillingFrequencyControls>
  );
}

function getPriceAndBilling(cost, [billingFrequency, setBillingFrequency]) {
  const { amounts } = cost || {};

  const hasAmounts = (amounts || []).length > 1;

  return {
    price: <PricingOverviewSectionPrice {...cost} billingFrequency={billingFrequency} />,
    billingFrequency: hasAmounts && (
      <PricingOverviewSectionBillingFrequency
        {...cost}
        billingFrequency={billingFrequency}
        setBillingFrequency={setBillingFrequency}
      />
    ),
  };
}

function PricingPlanColumnCTA({
  openGlobalPayrollModal,
  CustomCTAButton = TwoStepFormModalButton,
  promocode,
  ...plan
}) {
  const { query } = useRouter();

  const {
    primary: {
      call_to_action_button_v1: ctaGetQuote,
      call_to_action_modal_content_v1: ctaModalDemo,
      call_to_action_button_label_v1: ctaLabel,
    },
  } = plan;

  if (ctaGetQuote) {
    return ctaModalDemo ? (
      <CustomCTAButton openModal={toggleFormIfParam(query, 'scheduleDemo')}>
        {ctaLabel}
      </CustomCTAButton>
    ) : (
      <CTAButton tone="primary" shadow onClick={openGlobalPayrollModal}>
        {ctaLabel}
      </CTAButton>
    );
  }

  return (
    <CTAButton tone="primary" shadow href={autoApplyPromocode(promocode)} rel="nofollow">
      {ctaLabel}
    </CTAButton>
  );
}

function PricingPlanColumn({ openGlobalPayrollModal, CustomCTAButton, promocode, ...plan }) {
  const {
    primary: { name, description, cost, starts_open: startsOpen, open_cta_label: openCtaLabel },
    label: sliceLabel,
    fields: featuresList,
  } = plan;

  const {
    highlight_message: highlightMessage,
    display_international_pricing: displayInternationalPricing,
    international_pricing_label: internationalPricingLabel,
    international_pricing_table: internationalPricingTable,
    hide = false,
  } = cost || {};
  const amounts = cost?.amounts || [];

  const [billingFrequency, setBillingFrequency] = useState(amounts[0]?.label || '');

  const highlightProps =
    sliceLabel === 'highlight' ? { $highlight: true, $turquoiseIcon: true } : {};

  const pricingAndBilling = getPriceAndBilling(cost, [billingFrequency, setBillingFrequency]);
  const [showInternationalPricing, setInternatinalPricingVisible] = useState(false);

  return (
    <PlanColumn
      hidden={hide}
      title={
        <>
          {name}
          {highlightMessage && (
            <ColumnHighlightMessage id="free-price-description" aria-hidden="true">
              {RichText.render(highlightMessage)}
            </ColumnHighlightMessage>
          )}
        </>
      }
      {...pricingAndBilling}
      description={RichText.render(description)}
      startsOpen={startsOpen}
      features={featuresList.map(({ feature_name: feature }) => feature)}
      cta={
        <PricingPlanColumnCTA
          openGlobalPayrollModal={openGlobalPayrollModal}
          {...plan}
          CustomCTAButton={CustomCTAButton}
          promocode={promocode}
        />
      }
      openCtaLabel={openCtaLabel}
      footer={
        displayInternationalPricing &&
        internationalPricingLabel && (
          <PricingButtonWrapper>
            <InternationalPricingButton onClick={() => setInternatinalPricingVisible(true)}>
              {internationalPricingLabel}
            </InternationalPricingButton>
            <PricingDialog
              isOpen={showInternationalPricing}
              pricing={internationalPricingTable}
              onDismiss={() => setInternatinalPricingVisible(false)}
            />
          </PricingButtonWrapper>
        )
      }
      {...highlightProps}
    />
  );
}

export function PricingOverviewSection({
  pricingOverview,
  hubspotFormDefinition,
  tone,
  CustomCTAButton,
  disclaimer,
  promocode,
}) {
  const [displayGlobalPayrollModal, setDisplayGlobalPayrollModal] = useState(false);

  function openGlobalPayrollModal() {
    setDisplayGlobalPayrollModal(true);
  }
  function closeGlobalPayrollModal() {
    setDisplayGlobalPayrollModal(false);
  }

  const { pricing_overview_section: pricingPlans } = pricingOverview;

  return (
    <PricingSection $tone={tone} id="pricing">
      <ContentContainer>
        <PricingTable>
          {pricingPlans.map((plan, index) => (
            <PricingPlanColumn
              key={index}
              {...plan}
              openGlobalPayrollModal={openGlobalPayrollModal}
              CustomCTAButton={CustomCTAButton}
              promocode={promocode}
            />
          ))}
        </PricingTable>

        <GlobalPayrollFormModal
          hubspotFormDefinition={hubspotFormDefinition}
          visible={displayGlobalPayrollModal}
          onCancel={closeGlobalPayrollModal}
        />
        <DisclaimerContainer>
          <PrismicRichText field={disclaimer} />
        </DisclaimerContainer>
      </ContentContainer>
    </PricingSection>
  );
}

export function pricingTableChange(dataSlice, pricingOverview) {
  const {
    topHighlightMessage,
    annuallyPrice,
    monthlyPrice,
    firstCardTitle,
    secondCardTitle,
    thirdCardTitle,
    fourthCardTitle,
    firstCardDescription,
    secondCardDescription,
    thirdCardDescription,
    fourthCardDescription,
    secondCardPrice,
    secondCardPriceCrossed,
    secondCardPriceCrossedLabel,
    thirdCardPrice,
    fourthCardPrice,
    annuallyCrossed,
    monthlyCrossed,
    annuallyCrossedLabel,
    monthlyCrossedLabel,
    hideFirst,
    hideSecond,
    hideThird,
    hideFourth,
  } = transformProperties(dataSlice.primary);

  const entry = pricingOverview.pricing_overview_section;
  const changesCheckArray = [
    {
      index: 0,
      item: 'highlight',
      value: topHighlightMessage,
    },
    {
      index: 0,
      item: 'annually',
      value: annuallyPrice,
    },
    {
      index: 0,
      item: 'annuallyCrossed',
      value: annuallyCrossed,
    },
    {
      index: 0,
      item: 'annuallyCrossedLabel',
      value: annuallyCrossedLabel,
    },
    {
      index: 0,
      item: 'monthly',
      value: monthlyPrice,
    },
    {
      index: 0,
      item: 'monthlyCrossed',
      value: monthlyCrossed,
    },
    {
      index: 0,
      item: 'monthlyCrossedLabel',
      value: monthlyCrossedLabel,
    },
    {
      index: 0,
      item: 'name',
      value: firstCardTitle,
    },
    {
      index: 1,
      item: 'name',
      value: secondCardTitle,
    },
    {
      index: 2,
      item: 'name',
      value: thirdCardTitle,
    },
    {
      index: 3,
      item: 'name',
      value: fourthCardTitle,
    },
    {
      index: 1,
      item: 'price',
      value: secondCardPrice,
    },
    {
      index: 2,
      item: 'price',
      value: thirdCardPrice,
    },
    {
      index: 3,
      item: 'price',
      value: fourthCardPrice,
    },
    {
      index: 0,
      item: 'description',
      value: firstCardDescription,
    },
    {
      index: 1,
      item: 'description',
      value: secondCardDescription,
    },
    {
      index: 2,
      item: 'description',
      value: thirdCardDescription,
    },
    {
      index: 3,
      item: 'description',
      value: fourthCardDescription,
    },
    {
      index: 1,
      item: 'secondPriceCrossed',
      value: secondCardPriceCrossed,
    },
    {
      index: 1,
      item: 'secondPriceCrossedLabel',
      value: secondCardPriceCrossedLabel,
    },
    {
      index: 0,
      item: 'hideFirst',
      value: hideFirst,
    },
    {
      index: 1,
      item: 'hideSecond',
      value: hideSecond,
    },
    {
      index: 2,
      item: 'hideThird',
      value: hideThird,
    },
    {
      index: 3,
      item: 'hideFourth',
      value: hideFourth,
    },
  ];

  function changeText(index, item, value) {
    const tableItem = entry[index].primary;

    switch (item) {
      case 'name':
        tableItem.name = value;
        break;
      case 'price':
        tableItem.cost.price_label = value;
        break;
      case 'description':
        tableItem.description[0].text = value;
        break;
      case 'annually':
        tableItem.cost.amounts[0].amount = value;
        break;
      case 'monthly':
        tableItem.cost.amounts[1].amount = value;
        break;
      case 'highlight':
        tableItem.cost.highlight_message = value;
        break;
      case 'annuallyCrossed':
        tableItem.cost.amounts[0].cross_amount = value;
        break;
      case 'monthlyCrossed':
        tableItem.cost.amounts[1].cross_amount = value;
        break;
      case 'annuallyCrossedLabel':
        tableItem.cost.amounts[0].cross_label = value;
        break;
      case 'monthlyCrossedLabel':
        tableItem.cost.amounts[1].cross_label = value;
        break;
      case 'secondPriceCrossed':
        tableItem.cost.price_label_crossed = value;
        break;
      case 'secondPriceCrossedLabel':
        tableItem.cost.price_label_crossed_label = value;
        break;
      case 'hideFirst':
      case 'hideSecond':
      case 'hideThird':
      case 'hideFourth':
        tableItem.cost.hide = value;
        break;
      default:
        return null;
    }
    return null;
  }

  changesCheckArray.map(({ index, item, value }) => {
    if (value) {
      changeText(index, item, value);
    }
    return changesCheckArray;
  });

  return pricingOverview;
}
